import type { ReactNode } from 'react';
import { Badge } from '@/components/ui';
import { TextLink } from '@/components/ui/TextLink';
import type { FabCompanyFunnel, FabFunnelStage } from '@/lib/types';
import { PI_PRODUCTS, PiError, piCount, piPct, type PiProductKey } from './pi-kit';

/**
 * A company's SEED FAB card, plus the card shell every product in the company
 * page's "Product Insights" section uses (`CompanyInsightCard`).
 *
 * Since 2026-10-18 (owner ruling 3) this is ONE card of that section
 * (`CompanyProductInsightsSection`), which owns the section shell, the
 * permission check, the FAB read (`useCompanyFabFunnel`) and the cycle label.
 * Overhauled 2026-10-19: one grid of compact per-product cards.
 *
 * ⚠️ `hidden` (403/404, or no linked school) renders nothing. A failed read is
 * shown — inside the card — because hiding it would make "SEED FAB did not
 * respond" read as "this school has no applicants".
 *
 * The card shows the three stages summed over the linked schools (null when
 * any school does not report a stage — a partial sum would understate). Rates
 * are stage-to-stage: "admitted" = admits ÷ applications, "enrolled" =
 * enrolled ÷ admits. A zero denominator is "—", never 0%. With more than one
 * linked school each school gets its own line, linked to the Schools tab.
 */

type Stage = FabFunnelStage['stage'];

const STAGES: readonly Stage[] = ['applications', 'admits', 'enrolled'];

function rate(numerator: number | null, denominator: number | null): number | null {
  if (numerator === null || denominator === null || denominator <= 0) return null;
  return numerator / denominator;
}

function schoolsTabHref(schoolName: string): string {
  return `/product-insights/fab?tab=schools&q=${encodeURIComponent(schoolName)}`;
}

function productOf(key: PiProductKey): { label: string; to: string } {
  const product = PI_PRODUCTS.find((entry) => entry.key === key);
  return product ?? { label: key, to: '/product-insights/fab' };
}

export interface CompanyInsightFigure {
  label: string;
  value: string;
  detail?: string;
}

/** One product's card: name (a link to its page), an optional badge, 1–3 figures. */
export function CompanyInsightCard({
  product,
  to,
  badge,
  figures,
  children,
}: {
  product: PiProductKey;
  /** Overrides the product page link (e.g. a searched Schools tab). */
  to?: string;
  badge?: string;
  figures?: readonly CompanyInsightFigure[];
  children?: ReactNode;
}) {
  const meta = productOf(product);
  return (
    <li className="flex min-w-0 flex-col gap-3 rounded-sm border border-hairline bg-surface p-3">
      <div className="flex min-w-0 items-center justify-between gap-2">
        <h3 className="m-0 min-w-0 truncate text-sm font-semibold">
          <TextLink to={to ?? meta.to}>{meta.label}</TextLink>
        </h3>
        {badge !== undefined && (
          <Badge className="shrink-0" title={badge}>
            {badge}
          </Badge>
        )}
      </div>
      {figures !== undefined && figures.length > 0 && (
        <dl className="m-0 grid grid-cols-3 gap-2">
          {figures.map((figure) => (
            <div key={figure.label} className="flex min-w-0 flex-col gap-0.5">
              <dt className="truncate text-xs text-fg-muted" title={figure.label}>
                {figure.label}
              </dt>
              <dd className="m-0 text-lg font-semibold leading-tight tabular-nums text-fg">
                {figure.value}
              </dd>
              {figure.detail !== undefined && (
                <dd className="m-0 truncate text-xs tabular-nums text-fg-secondary">{figure.detail}</dd>
              )}
            </div>
          ))}
        </dl>
      )}
      {children}
    </li>
  );
}

export type CompanyFabLoad =
  | { status: 'loading' }
  | { status: 'hidden' }
  | { status: 'error'; message: string }
  | { status: 'ready'; funnel: FabCompanyFunnel };

function totalsOf(stages: readonly FabFunnelStage[]): Record<Stage, number | null> {
  const totals: Record<Stage, number | null> = { applications: null, admits: null, enrolled: null };
  for (const entry of stages) totals[entry.stage] = entry.total;
  return totals;
}

function figuresOf(totals: Record<Stage, number | null>): CompanyInsightFigure[] {
  return [
    { label: 'Applications', value: piCount(totals.applications) },
    {
      label: 'Admits',
      value: piCount(totals.admits),
      detail: `${piPct(rate(totals.admits, totals.applications))} admitted`,
    },
    {
      label: 'Enrolled',
      value: piCount(totals.enrolled),
      detail: `${piPct(rate(totals.enrolled, totals.admits))} enrolled`,
    },
  ];
}

/** The SEED FAB card: the funnel over the linked schools, or the read's error. */
export function CompanyFabFunnelBlock({ load, onRetry }: { load: CompanyFabLoad; onRetry?: () => void }) {
  if (load.status === 'loading' || load.status === 'hidden') return null;

  if (load.status === 'error') {
    return (
      <CompanyInsightCard product="fab">
        <PiError message={load.message} {...(onRetry !== undefined ? { onRetry } : {})} />
      </CompanyInsightCard>
    );
  }

  const schools = load.funnel.schools.map((school) => ({ ...school, totals: totalsOf(school.stages) }));
  const summed: Record<Stage, number | null> = { applications: 0, admits: 0, enrolled: 0 };
  for (const stage of STAGES) {
    let sum: number | null = 0;
    for (const school of schools) {
      const value = school.totals[stage];
      sum = sum === null || value === null ? null : sum + value;
    }
    summed[stage] = schools.length === 0 ? null : sum;
  }
  const single = schools.length === 1 ? schools[0] : undefined;

  return (
    <CompanyInsightCard
      product="fab"
      {...(single !== undefined ? { to: schoolsTabHref(single.name) } : {})}
      figures={figuresOf(summed)}
    >
      {single === undefined && schools.length > 1 && (
        <ul className="m-0 list-none divide-y divide-hairline border-t border-hairline p-0">
          {schools.map((school) => (
            <li
              key={school.schoolId}
              className="flex min-w-0 items-baseline justify-between gap-3 py-1.5 text-xs last:pb-0"
            >
              <TextLink to={schoolsTabHref(school.name)} tone="muted" className="min-w-0 truncate">
                {school.name}
              </TextLink>
              <span className="shrink-0 tabular-nums text-fg-secondary">
                {STAGES.map((stage) => piCount(school.totals[stage])).join(' · ')}
              </span>
            </li>
          ))}
        </ul>
      )}
    </CompanyInsightCard>
  );
}
